<template>
  <b-container fluid>
    <b-row>
      <b-col>
        <div>
          <!-- TITLE -->
          <div class="mb-3 text-center" style="font-size: 14px; font-weight: bold">
            {{ $tr('stacked-bar-chart-title', 'Quantity and Summary Color Versions by Status for each Merchant') }}
          </div>
          <form class="mt-3 form-inline d-flex justify-content-center">
            <div class="form-group">
              <!-- SEASON SELECT -->
              <label class="mr-3">{{ $tr('stacked-bar-dashboard-season', 'Season') }}</label>
              <b-form-select
                  class="mr-3"
                  v-model="selectedSeasonId"
                  :options="seasonOptions"
                  size="sm"
              ></b-form-select>
            </div>
          </form>

          <!-- MERCHANT SELECT -->
          <b-row v-if="editMode">
            <b-col class="col-12">
              <Multiselect
                  class="pt-1 pb-1"
                  v-model="selectedMerchants"
                  :style="'min-width: 400px; width: ' + (80 + selectedMerchants.length * 100) + 'px'"
                  :items="merchantOptions"
                  :readOnly="false"
                  @item-removed="handleItemRemoved">
              </Multiselect>
            </b-col>
          </b-row>

          <!-- LOADING -->
          <b-row
              v-if="loading"
              no-gutters
              style="height: 420px; justify-content: center"
              class="mt-2">
            <b-col class="col-12">
              <loading></loading>
            </b-col>
          </b-row>

          <!-- CHART -->
          <b-row
              class="mt-2"
              v-if="this.chartData.data[0] && !loading">
            <b-col class="col-12">
              <div class="d-block w-100" style="overflow: auto">
                <div
                    :style="'max-height: 420px; min-height: 420px; width: ' + (80 + selectedMerchants.length * 80) + 'px'">
                  <canvas
                      ref="stackedChartCanvas"
                      :id="'stackedChartCanvas' + id"
                  ></canvas>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- CHOOSE A SEASON -->
          <div
              v-if="!selectedSeasonId && !loading"
              class="text-center mt-3"
              style="font-weight: bold; color: rgb(255, 99, 132)"
              v-text="$tr('stacker-bar-dashboard-choose-season', 'Choose a Season')">
          </div>

          <!-- CHOOSE MERCHANTS -->
          <div
              v-if="!this.selectedMerchants[0] && selectedSeasonId && !loading"
              class="text-center mt-3"
              style="font-weight: bold; color: rgb(255, 99, 132)"
              v-text="$tr('stacker-bar-dashboard-choose-merchants', 'Choose Merchants')"
          >
          </div>

          <!-- NO DATA INFO -->
          <div
              v-if="!this.chartData.data[0] && this.selectedMerchants[0] && selectedSeasonId && !loading"
              class="text-center mt-3"
              style="font-weight: bold; color: rgb(255, 99, 132)"
              v-text="$tr('stacker-bar-dashboard-no-such-data', 'No Such Data Found')"
          >
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Loading from "@/components/layout/Loading.vue";
import DashletsRpc from "@/DashletsRpc";
import Chart from 'chart.js/auto';
import Multiselect from "@/components/inputs/controls/Multiselect.vue";
import DashletsHome from "@/components/dashboard/DashletsHome.vue";

export default {
  name: "StackedBarChart",
  components: {
    Loading,
    Multiselect,
    DashletsHome
  },

  async mounted() {
    this.seasons = await DashletsRpc.DashboardsGeneral.getSeasonList();
    this.seasonOptions = this.seasons.map(season => ({
      value: season.id,
      text: season.season
    }));

    this.merchants = await DashletsRpc.DashboardsGeneral.getApplicationUserList();
    this.merchantOptions = this.merchants.map(merchant => ({
      value: merchant.id,
      text: merchant.email
    }));


    // Load saved selected season id
    this.selectedSeasonId = this.config.selected;
    if (!this.seasons.find(season => season.id === this.selectedSeasonId)) {
      this.selectedSeasonId = null;
      return;
    }
    // Load saved selected merchants
    if (this.config.selectedMerchants) {
      this.selectedMerchants = this.config.selectedMerchants.map(id =>
          this.merchantOptions.find(merchant => merchant.value === id)
      ).filter(Boolean); // Remove any merchants that aren't found
    }

    this.loading = false;
    this.$emit("resize", "col-auto");
  },

  methods: {
    async getDataToChart() {
      if (this.selectedMerchants.length === 0) {
        this.chartData.data = [];
        this.loading = false;
        this.updateChart();
        return;
      }

      this.loading = true;
      let data = await DashletsRpc.StackedBarChart.getDataForMerchants(
          this.selectedSeasonId,
          this.selectedMerchants.map(merchant => merchant.value)
      );
      this.chartData.data = data.map(merchant => [
        merchant.colorVersionsConfirmed,
        merchant.colorVersionsHavingAnOffer,
        merchant.colorVersionsInOrder
      ]);

      this.$nextTick(() => {
        this.updateChart();
      });
      this.loading = false;
    },

    setupChart() {
      const canvas = document.getElementById('stackedChartCanvas' + this.id);
      canvas.removeAttribute("width");
      canvas.removeAttribute("height");
      canvas.style.height = "";
      canvas.style.width = "";

      this.$nextTick(() => {
        const ctx = canvas.getContext('2d');
        const labels = this.selectedMerchants.map(merchant => merchant.text);
        const data = {
          labels: labels,
          datasets: [
            {
              label: this.chartData.label[2],
              data: this.chartData.data.map(d => d[2]),
              backgroundColor: this.chartData.color[2]
            },
            {
              label: this.chartData.label[1],
              data: this.chartData.data.map(d => d[1]),
              backgroundColor: this.chartData.color[1]
            },
            {
              label: this.chartData.label[0],
              data: this.chartData.data.map(d => d[0]),
              backgroundColor: this.chartData.color[0]
            }
          ]
        };

        const config = {
          type: "bar",
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                stacked: true
              },
              y: {
                stacked: true
              }
            }
          }
        };

        this.chart = new Chart(ctx, config);
      });
    },

    updateChart() {
      if (this.chart) {
        if (typeof this.chart.destroy === 'function') {
          this.chart.destroy();
        }
        this.chart = null; // Wyzeruj referencję
      }
      this.$nextTick(() => {
        this.setupChart(); // Utwórz nowy wykres
      });
    },
    handleItemRemoved(item) {
      this.selectedMerchants = this.selectedMerchants.filter(merchant => merchant.value !== item.value);
      this.getDataToChart();  // Update data chart after remove item
    }
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },

  props: {
    uuid: {type: String, default: ""},
    dashletConfig: {type: Object},
    config: {type: Object},
    editMode: {type: Boolean, default: false},
    id: {type: Number, required: true}
  },

  watch: {
    "selectedSeasonId": async function () {
      this.config.selected = this.selectedSeasonId;
      this.$emit("config-change");
      await this.getDataToChart();
    },
    "selectedMerchants": function (newVal) {
      this.config.selectedMerchants = newVal.map(merchant => merchant.value);
      this.$emit("config-change");
      this.getDataToChart();
      this.$emit("resize", "col-auto");
    },
  },

  data() {
    return {
      loading: true,
      seasons: [],
      seasonOptions: [],
      selectedSeasonId: null,
      merchants: [],
      merchantOptions: [],
      selectedMerchants: [],
      chartData: {
        label: [
          this.$tr("stacked-bar-chart-label-confirmed", "Color Versions Confirmed"),
          this.$tr("stacked-bar-chart-label-offered", "Color Versions Offered"),
          this.$tr("stacked-bar-chart-label-ordered", "Color Versions Ordered")
        ],
        data: [],
        color: ["rgb(255, 99, 132)", "rgb(4, 110, 180)", "rgb(48, 213, 200)"]
      }
    };
  }
};
</script>

<style scoped>
</style>
